import { useState } from 'react'
import type { FC } from 'react'

import { F0Button } from '@factorialco/f0-react'
import { UpsellGlpButton, UpsellLandingPage } from 'generated/resources/datatable.graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useButtonActions from 'modules/discover/hooks/useButtonActions'
import useActiveSubscriptionOverload from 'modules/marketplace_modules/hooks/useActiveSubscriptionOverload'
import { useModuleStatus } from 'modules/marketplace_modules/hooks/useModuleStatus'

interface GlpButtonBlockProps {
  block: UpsellGlpButton
  slug: string
  landingPage: UpsellLandingPage
  currentUpsellOrigin: {
    request_source: string
    request_source_category?: string
    request_source_component?: string
  } | null
}

export const GlpButtonBlock: FC<GlpButtonBlockProps> = ({
  block,
  slug,
  landingPage,
  currentUpsellOrigin,
}) => {
  const { company } = useCurrentSession()
  const [isButtonLoading, setIsButtonLoading] = useState(false)
  const { addLineItemMutation, handleSubscriptionOverloadMutation, handleUpsellRequestMutation } =
    useButtonActions({
      billingAddon: landingPage?.moduleRelated?.billingAddonReference?.[0] || null,
      moduleName: landingPage?.moduleRelated?.chargebeeModuleName || '',
      currentUpsellOrigin: currentUpsellOrigin,
      featureId: landingPage?.featureAvailabilityId || '',
      subscriptionExpiresIn: block?.subscriptionExpiresIn || '',
      chargebeeAddonId: block?.chargebeeAddonId || '',
    })

  const billingAddonReference = landingPage?.moduleRelated?.billingAddonReference ?? []
  const addonReferences = billingAddonReference.length > 0 ? [billingAddonReference] : []
  const { getModuleStatus } = useModuleStatus(addonReferences, {})
  const { activeSubscriptionOverload } = useActiveSubscriptionOverload([company.id])

  if (!block) {
    return null
  }

  const actionType = block?.actionType || 'redirect'
  const isAddonAcquired = Boolean(
    activeSubscriptionOverload?.addonIds.includes(block?.chargebeeAddonId || '')
  )
  const moduleStatus = getModuleStatus({
    moduleName: landingPage?.moduleRelated?.chargebeeModuleName ?? null,
    billingAddonReference: billingAddonReference.length > 0 ? billingAddonReference : null,
    moduleIncludedInPlan: [],
  })
  const isModuleAcquired = moduleStatus.isInstalled

  const handleAddLineItem = async () => {
    if (isButtonLoading) return

    try {
      setIsButtonLoading(true)

      const result = await addLineItemMutation()
      if (result) {
        track(trackingEvents.upsell.addedLineItem, {
          source: `discover-${slug} landing`,
        })
        app.setFlash({
          type: 'correct',
          message: i18n.t('growth.landing_creator.buttons.add_line_item.success'),
        })
        window.location.reload()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('growth.landing_creator.buttons.add_line_item.error'),
        })
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('growth.landing_creator.buttons.add_line_item.error'),
      })
    } finally {
      setIsButtonLoading(false)
    }
  }

  const showSuccessAndRedirect = () => {
    app.setFlash({
      type: 'correct',
      message: i18n.t('growth.landing_creator.buttons.add_line_item.success'),
    })
    if (block?.url) {
      window.location.href = block.url
    } else {
      window.location.reload()
    }
  }

  const showError = () => {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('growth.landing_creator.buttons.add_line_item.error'),
    })
  }

  const handleSubscriptionOverload = async () => {
    if (isButtonLoading) return

    try {
      setIsButtonLoading(true)

      if (!activeSubscriptionOverload) {
        const response = await handleSubscriptionOverloadMutation()
        const result = handleResult(response, () => undefined)

        if (!result.success) {
          showError()
          return
        }

        track(trackingEvents.upsell.addedLineItem, {
          source: `discover-${slug} landing`,
        })

        if (block?.upsellRequest) {
          await handleUpsellRequestMutation()
        }

        showSuccessAndRedirect()
      } else {
        const response = await handleUpsellRequestMutation()
        const result = handleResult(response, () => undefined)

        if (!result.success) {
          showError()
          return
        }

        showSuccessAndRedirect()
      }
    } catch {
      showError()
    } finally {
      setIsButtonLoading(false)
    }
  }

  const handleLabel = () => {
    if (actionType === 'subscription_overload' && isAddonAcquired) {
      return block?.secondaryLabel
    }
    if (actionType === 'redirect' && isModuleAcquired) {
      return block?.labelModuleAcquired !== '' ? block?.labelModuleAcquired : block?.label
    }
    return block?.label
  }

  const handleButtonClick = () => {
    track(trackingEvents.discover.clickGlpButton, {
      component: 'glp_button',
      action: block.label,
    })

    if (actionType === 'redirect') {
      const redirectUrl = isModuleAcquired
        ? block?.redirectUrlModule || block?.redirectUrl || block?.url
        : block?.redirectUrl || block?.url
      if (block?.opensInNewTab) {
        window.open(redirectUrl, '_blank')
      } else {
        window.location.href = redirectUrl
      }
    } else if (actionType === 'add_line_item') {
      handleAddLineItem()
    } else if (actionType === 'subscription_overload') {
      handleSubscriptionOverload()
    }
  }

  const handleButtonDisabled = () => {
    if (actionType === 'subscription_overload') {
      return isAddonAcquired
    }
    return false
  }

  return (
    <F0Button
      key={block?.id}
      label={handleLabel()}
      variant={block?.style == 'primary_btn' ? 'default' : 'outline'}
      onClick={handleButtonClick}
      loading={isButtonLoading}
      disabled={handleButtonDisabled()}
    />
  )
}
